// @ts-check
/** @typedef {import('../../components/popover-card/types').ButtonOption} ButtonOption */
/** @typedef {import('./types').CohortDetail} CohortDetail */

import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {
  buildCreateEntityModalAttrs,
  isSharingEnabledForProject,
  normalizeCohortEntity,
} from 'common/report/util/saved-entities';
import {EntityType} from 'common/report/util/saved-entities/types';
import {getProjectId} from 'common/report/util';
import {currentProjectStore} from 'common/report/stores/project-details';
import {useStore} from 'common/util/component-helpers';

import {createEntityModalPromise} from '../create-entity-modal';
import {ModalReturn} from '../create-entity-modal/types';
import css from './index.styl?inline';
import {template} from './template';
import {ActionsPopoverAttrs, ActionsPopoverState, CohortDetail, VIEWS} from './types';

const STATE_RESET = Object.freeze({
  cohortModalIsOpen: false,
  cohortDetail: null,
  dropMenuOpen: false,
  keydownProxyEl: null,
  searchFilter: ``,
  view: null,
} as ActionsPopoverState);

function inCohortDetails(cohortDetails: CohortDetail[], type) {
  const types = cohortDetails.map((detail) => detail.type);
  return types.includes(type);
}

function findCohortDetail(cohortDetails: CohortDetail[], type) {
  return cohortDetails.find((detail) => detail.type === type);
}

/**
 * ActionsPopover is responsible for rendering an mp-popover-card component
 *
 * Depending on which item you click, ActionsPopover will either emit an 'action' event and
 * close the component or display another view.
 */
export class ActionsPopover extends Component<ActionsPopoverState, ActionsPopoverAttrs> {
  static get attrsSchema(): AttrsSchema<ActionsPopoverAttrs> {
    return {
      ...super.attrsSchema,
      open: {type: `boolean`},

      // attributes for VIEWS.POPOVER
      'is-segmented': {type: `boolean`},
      title: {type: `string`},
      subtitle: {type: `string`},
      /**
       * button-options are passed directly to mp-popover-card
       * the next view after selecting a button option in the popover card is dictated
       * by the `value` of the button option
       * 1. value === VIEWS.BREAKDOWN => the next view will be VIEWS.BREAKDOWN
       * 2. value === the value of `type` in one of cohort-details => the next view will be VIEWS.CREATE_COHORT
       * 3. if value doesn't match any of the above => the popover will close
       */
      'button-options': {type: `json`, description: `interface: ButtonOption[]`, default: []},

      // attributes for VIEWS.BREAKDOWN
      'breakdown-options': {type: `json`, default: {}},
      event: {type: `string`},
      'project-id': {type: `number`},
      'workspace-id': {type: `number`},

      // attributes for VIEWS.CREATE_COHORT
      'cohort-details': {type: `json`, description: `interface: CohortDetail[]`, default: []},
      'cohort-names': {
        type: `json`,
        description: `interface: string[] - the names of your cohorts. Used for validation.`,
        default: [],
      },
      'prevent-close-selectors': {
        type: `json`,
        description: `interface: string[] - list of selectors that shouldn't trigger drop menu to close`,
        default: [],
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {...STATE_RESET},
      helpers: {
        handlePopoverAction: (ev) => {
          const {value} = ev.detail.item;
          if (inCohortDetails(this.attr(`cohort-details`), value)) {
            const cohortDetail = findCohortDetail(this.attr(`cohort-details`), value);
            this.update({
              view: VIEWS.CREATE_COHORT,
              cohortDetail,
              cohortModalIsOpen: true,
            });
            this.dispatchEvent(new CustomEvent(`openCohortModal`, {detail: {cohortDetail}}));

            createEntityModalPromise(this.helpers.getCohortModalAttributes(), {
              existingEntityNames: this.attr(`cohort-names`),
            }).then((result) => {
              if (result.didSave) {
                this.helpers.handleCohortSaved(result);
              } else {
                this.helpers.handleCohortCanceled(result);
              }
            });
          } else if (value === VIEWS.BREAKDOWN) {
            this.update({view: VIEWS.BREAKDOWN});
          } else {
            this.dispatchEvent(new CustomEvent(`action`, {detail: {action: value}}));
            this.update({dropMenuOpen: false});
          }
        },
        handlePopoverClose: () => {
          this.dispatchEvent(new CustomEvent(`close`));
        },
        handleCohortSaved: (ev: ModalReturn) => {
          const {name, description} = ev;
          const {cohortDetail} = this.state;
          this.update({cohortModalIsOpen: false});
          this.dispatchEvent(
            new CustomEvent(`action`, {
              detail: {
                action: `createCohort`,
                value: {
                  name,
                  description,
                  type: cohortDetail.type,
                  shouldShare: ev.event === `saveAndShare`,
                },
              },
            }),
          );
        },
        handleCohortCanceled: (ev: ModalReturn) => {
          const {name, description} = ev;
          const {cohortDetail} = this.state;
          this.dispatchEvent(new CustomEvent(`cancelCohort`, {detail: {name, description, type: cohortDetail.type}}));
          this.update({cohortModalIsOpen: false});
        },
        handleBreakdownMenuSelect: (ev) => {
          this.dispatchEvent(
            new CustomEvent(`action`, {
              detail: {
                action: `breakdown`,
                value: ev.detail.item,
              },
            }),
          );
        },
        breakdownDisabledItemsMap: () =>
          (this.attr(`breakdown-options`).disabledItems || []).reduce((map, item) => ({...map, [item]: true}), {}),
        addKeydownProxyEl: (vnode) => {
          this.update({keydownProxyEl: vnode.elm});
          requestAnimationFrame(() => vnode.elm.focus());
        },
        handleSearchInput: (ev) => this.update({searchFilter: ev.target.value}),
        handleDropMenuChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.helpers.handlePopoverClose();
          }
        },

        getProjectId: () => this.attr(`project-id`) || parseInt(getProjectId(), 10),
        getCohortModalAttributes: () => {
          const {warning} = this.state.cohortDetail;
          const attributes = buildCreateEntityModalAttrs({
            projectId: this.helpers.getProjectId(),
            entityType: EntityType.Cohort,
            modalOpen: this.state.cohortModalIsOpen,
            isEditing: false,
            isDuplicating: false,
            entity: normalizeCohortEntity(this.state.cohortDetail),
            showShareManagement: this.hasSharingFeatureFlag(),
          });
          if (warning) {
            attributes.warning = warning;
          }
          return attributes;
        },
        getCohortModalProps: () => {
          return {
            existingEntityNames: this.attr(`cohort-names`),
          };
        },
      },
    };
  }

  hasSharingFeatureFlag() {
    return isSharingEnabledForProject(currentProjectStore.value);
  }

  connectedCallback() {
    super.connectedCallback();
    useStore(this, currentProjectStore);
    currentProjectStore.fetch();
  }

  attributeChangedCallback(attr: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `open`) {
      if (this.attr(`open`)) {
        this.update({dropMenuOpen: true});
      } else if (this.initialized) {
        this.reset();
      }
    }
  }

  reset() {
    this.update(STATE_RESET);
  }
}

export const ActionsPopoverEl = defineAndMakeJsxFactory(`mp-actions-popover`, ActionsPopover);
